import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { isElectron } from '#app-config';
import {
  useInstrumentWithDetails,
  useTokenizedApi,
  useUserDetails,
  usePrimaryLockbox,
  useLocaleCurrency,
  CURRENCY_USD,
  usePendingWorkspaces,
  useProfiles,
  usePersonalProfile,
  useProfile,
} from '@tokenized/sdk-react-private';
import { openExternalLink } from '#app-config';
import {
  FEATURE_FLAG_BILLING_INTERNAL,
  FEATURE_FLAG_BILLING_SIMULATE_NEAR_LIMIT,
  FEATURE_FLAG_BILLING_SIMULATE_EXCEEDED_LIMIT,
  FEATURE_FLAG_BILLING_SIMULATE_OVERDUE,
  FEATURE_FLAG_BILLING_SIMULATE_SUSPENDED,
} from '#config/features';
import { runningFromSource } from '#config/definitions';
import { isDebugBuild } from '#config/shared';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import { useLiquidNobleActiveProfiles } from '#modules/api/User';
import { jsonKeyTransform, url, toCamelCase, toSnakeCase } from './network.js';

const DAY_MILLISECONDS = 1000 * 60 * 60 * 24;

// For simulating billing issues
const randomNumbers = [
  Math.random(),
  Math.random(),
  Math.random(),
  Math.random(),
];

const queryKeyPrefix = 'billing';

function makeCallbackURL() {
  let redirectPage = 'desktop-app';
  if (runningFromSource) {
    redirectPage = 'desktop-app-from-src';
  } else if (isDebugBuild) {
    redirectPage = 'desktop-app-development';
  }

  return `${window.location}`;
}

function parseRfc3339Timestamp(value) {
  if (typeof value === 'string') {
    const timestamp = new Date(value);
    if (!isNaN(timestamp)) {
      return timestamp;
    }
  }
}

export function useSubscriptions(profileId) {
  const tokenizedApi = useTokenizedApi();

  const { email: accountEmail } = useUserDetails() || {};

  return useQuery([queryKeyPrefix, 'subscriptions', profileId], async () => {
    if (!profileId) return;
    return jsonKeyTransform(
      (
        await tokenizedApi.endpoints.get(
          url`profiles/${profileId}/subscriptions`,
        )
      ).data.data,
      toCamelCase,
    );
  }).data?.map((subscription) => ({
    ...subscription,
    paymentEmail: accountEmail,
  }));
}

async function getWorkspaceStatus(tokenizedApi, profileId, entityId) {
  return jsonKeyTransform(
    (
      await tokenizedApi.endpoints.get(
        profileId
          ? url`profiles/${profileId}/workspace/status`
          : url`entities/${entityId}/workspace/status`,
      )
    ).data.data,
    toCamelCase,
  );
}

export function useWorkspaceStatus(profileId, entityId) {
  const tokenizedApi = useTokenizedApi();

  const queryResult = useQuery(
    [queryKeyPrefix, 'workspaceStatus', profileId, entityId],
    async () =>
      !profileId && !entityId
        ? null
        : await getWorkspaceStatus(tokenizedApi, profileId, entityId),
  );

  const statusResponse = queryResult.data;

  const currency = useInstrumentWithDetails({
    currencyCode: statusResponse?.currency,
  });

  const lockbox = usePrimaryLockbox(profileId);
  const administratorCount = lockbox?.signers?.length;

  if ((!profileId && !entityId) || !queryResult.isFetched) {
    return undefined;
  }

  const now = Date.now();
  const suspensionDays = statusResponse?.suspensionDays || 30;
  const lastBalance = currency.withQuantity({
    quantity: statusResponse?.lastBalance,
  });

  const transformedStatus = {
    ...statusResponse,
    currency: currency,
    isSubscribed: !!statusResponse?.currency,
    isTreasuryLimitExceeded: false,
    isInvoiceOverdue: false,
    dateExceeded: undefined,
    datePaymentFailed: undefined,
    suspensionDays,
    daysUntilSuspensionForExceededLimit: undefined,
    daysUntilSuspensionForInvoiceOverdue: undefined,
    daysUntilSuspension: undefined,
    treasuryLimit: null,
    lastBalance,
    percentageOfTreasuryLimit: undefined,
    lastCheck: parseRfc3339Timestamp(statusResponse?.lastCheck),
    suspended: false,
    administratorCount,
    ready: !!statusResponse,
  };

  if (!transformedStatus.isSubscribed) {
    // Only simulate billing issues for subscribed workspaces
    return transformedStatus;
  }

  if (FEATURE_FLAG_BILLING_SIMULATE_NEAR_LIMIT) {
    const fakePercentage = 90.1 + randomNumbers[0] * 9.8;
    if (!transformedStatus.treasuryLimit?.hasQuantity) {
      transformedStatus.treasuryLimit = currency.withQuantity({
        amount: 1000,
      });
    }
    transformedStatus.percentageOfTreasuryLimit = fakePercentage;
    transformedStatus.lastBalance =
      transformedStatus.treasuryLimit.withQuantity({
        amount: (transformedStatus.treasuryLimit.amount * fakePercentage) / 100,
      });
  }
  if (FEATURE_FLAG_BILLING_SIMULATE_EXCEEDED_LIMIT) {
    if (!transformedStatus.treasuryLimit?.hasQuantity) {
      transformedStatus.treasuryLimit = currency.withQuantity({
        amount: 1000,
      });
    }
    transformedStatus.isTreasuryLimitExceeded = true;
    let fakeExpiryTime =
      randomNumbers[1] * transformedStatus.suspensionDays * DAY_MILLISECONDS;
    if (FEATURE_FLAG_BILLING_SIMULATE_SUSPENDED) {
      fakeExpiryTime = -fakeExpiryTime;
    }
    transformedStatus.dateExceeded = new Date(
      now + fakeExpiryTime - transformedStatus.suspensionDays,
    );
    const fakeDaysUntilSuspension = Math.floor(
      fakeExpiryTime / DAY_MILLISECONDS,
    );
    transformedStatus.daysUntilSuspensionForExceededLimit =
      fakeDaysUntilSuspension;
    if (
      !transformedStatus.daysUntilSuspension ||
      fakeDaysUntilSuspension < transformedStatus.daysUntilSuspension
    ) {
      transformedStatus.daysUntilSuspension = fakeDaysUntilSuspension;
    }
    if (!(transformedStatus.percentageOfTreasuryLimit >= 100)) {
      const fakePercentage = 101 + randomNumbers[2] * 100;
      transformedStatus.percentageOfTreasuryLimit = fakePercentage;
      transformedStatus.lastBalance =
        transformedStatus.treasuryLimit.withQuantity({
          amount:
            (transformedStatus.treasuryLimit.amount * fakePercentage) / 100,
        });
    }
  }
  if (
    FEATURE_FLAG_BILLING_SIMULATE_OVERDUE &&
    !transformedStatus.isInvoiceOverdue
  ) {
    transformedStatus.isInvoiceOverdue = true;
    let fakeExpiryTime =
      randomNumbers[2] * transformedStatus.suspensionDays * DAY_MILLISECONDS;
    if (FEATURE_FLAG_BILLING_SIMULATE_SUSPENDED) {
      fakeExpiryTime = -fakeExpiryTime;
    }
    const fakeDaysUntilSuspension = Math.floor(
      fakeExpiryTime / DAY_MILLISECONDS,
    );
    transformedStatus.daysUntilSuspensionForInvoiceOverdue =
      fakeDaysUntilSuspension;
    if (
      !transformedStatus.daysUntilSuspension ||
      fakeDaysUntilSuspension < transformedStatus.daysUntilSuspension
    ) {
      transformedStatus.daysUntilSuspension = fakeDaysUntilSuspension;
    }
  }
  if (FEATURE_FLAG_BILLING_SIMULATE_SUSPENDED) {
    transformedStatus.suspended = true;
  }

  return transformedStatus;
}

export function useIsWorkspaceSuspended(profileId) {
  const selectedProfileId = useSelectedProfileId();
  const profile = useProfile(profileId || selectedProfileId);
  return useWorkspaceStatus(profile?.id, profile?.entityId)?.suspended;
}

export function usePrices(category, currencyCode, discountCode = '') {
  const tokenizedApi = useTokenizedApi();

  return useQuery(
    [queryKeyPrefix, 'prices', category, currencyCode, discountCode],
    async () => {
      const pricesResponse = await jsonKeyTransform(
        (
          await tokenizedApi.endpoints.get(
            currencyCode
              ? url`stripe/prices/category/${category}/${currencyCode}?discountCode=${discountCode}`
              : category
                ? url`stripe/prices/category/${category}?discountCode=${discountCode}`
                : url`stripe/prices?discountCode=${discountCode}`,
          )
        ).data.data,
        toCamelCase,
      );

      return pricesResponse.map((price) => {
        const transformedPrice = { ...price };

        transformedPrice.isFree = !price.price;

        transformedPrice.allowedAdministrators = price.administratorLimit;

        return transformedPrice;
      });
    },
  ).data;
}

export function usePrice(priceId, currencyCode) {
  const tokenizedApi = useTokenizedApi();

  return useQuery(
    [queryKeyPrefix, 'prices', priceId, currencyCode],
    async () => {
      if (!priceId || !currencyCode) return;
      return await jsonKeyTransform(
        (
          await tokenizedApi.endpoints.get(
            url`stripe/prices/id/${priceId}/${currencyCode}`,
          )
        ).data.data,
        toCamelCase,
      );
    },
  ).data;
}

export function useSupportedCurrenciesOfAccountSet() {
  const tokenizedApi = useTokenizedApi();

  const currencies = useQuery([queryKeyPrefix, 'currencies'], async () => {
    return await jsonKeyTransform(
      (await tokenizedApi.endpoints.get(url`stripe/currencies`)).data.data,
      toCamelCase,
    );
  }).data;

  return useMemo(() => {
    const supportedCurrencies = new Set([CURRENCY_USD]);
    currencies?.forEach((currency) => {
      if (currency) {
        supportedCurrencies.add(currency);
      }
    });
    return supportedCurrencies;
  }, [currencies]);
}

export function useCurrencyOfAccount(profileId) {
  let defaultCurrencyCode = useLocaleCurrency();
  const supportedCurrencies = useSupportedCurrenciesOfAccountSet();
  if (!supportedCurrencies.has(defaultCurrencyCode)) {
    defaultCurrencyCode = CURRENCY_USD;
  }

  const workspaceStatus = useWorkspaceStatus(profileId);
  let currencyCode =
    workspaceStatus?.currency?.currencyCode || defaultCurrencyCode;

  return useInstrumentWithDetails({ currencyCode }, profileId);
}

function addDisplayDataToContractPrice({ price, currency }) {
  if (!price) {
    return price;
  }
  if (currency?.currencyCode !== price.currency) {
    currency = undefined;
  }

  const transformedPrice = {
    ...price,
    currency,
    price:
      currency && !price.tiers?.length && (price.price || price.price === 0)
        ? currency.withQuantity({ quantity: price.price })
        : undefined,
    tiers: price.tiers?.map((tier) => ({
      ...tier,
      flatCost:
        currency && (tier.flatCost || tier.flatCost === 0)
          ? currency.withQuantity({ amount: tier.flatCost / 100 })
          : undefined,
      unitCost:
        currency && (tier.unitCost || tier.unitCost === 0)
          ? currency.withQuantity({ amount: tier.unitCost / 100 })
          : undefined,
      upto: tier.upto === 0 ? Infinity : tier.upto,
    })),
  };

  return transformedPrice;
}

export function useWarmCurrenciesCache() {
  useCurrencyOfAccount();
}

export function useContractPrices({
  profileId,
  instrumentType,
  precision,
  contractSubscription,
  discountCode = '',
} = {}) {
  const currencyOfAccount = useCurrencyOfAccount(profileId);

  const basePrices = usePrices(
    'contract',
    currencyOfAccount?.currencyCode,
    discountCode,
  );
  const perUnitPrices = usePrices(
    'instruments',
    currencyOfAccount?.currencyCode,
    discountCode,
  );

  let [basePriceId, usagePriceId] =
    contractSubscription?.priceId.split(',') || [];

  const subscriptionBasePrice = usePrice(
    basePriceId,
    currencyOfAccount?.currencyCode,
  );
  const subscriptionUsagePrice = usePrice(
    usagePriceId,
    currencyOfAccount?.currencyCode,
  );

  const basePrice =
    subscriptionBasePrice ||
    basePrices?.find(
      (price) =>
        price.billingCycle === 'month' &&
        instrumentType === price.instrumentType,
    );

  const usagePrice =
    subscriptionUsagePrice ||
    perUnitPrices?.find(
      (price) =>
        price.billingCycle === 'month' &&
        instrumentType === price.instrumentType &&
        `${precision}` === price.precision,
    );

  return {
    basePrice: addDisplayDataToContractPrice({
      price: basePrice,
      currency: currencyOfAccount,
    }),
    perUnitPrice: addDisplayDataToContractPrice({
      price: usagePrice,
      currency: currencyOfAccount,
    }),
  };
}

export function refreshQueries(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries([queryKeyPrefix, 'subscriptions']),
    queryClient.invalidateQueries([queryKeyPrefix, 'workspaceStatus']),
  ]);
}

async function waitForBillingSSE(tokenizedApi, listenForSubscriptionSignal) {
  await new Promise((resolve) => {
    listenForSubscriptionSignal?.addEventListener('abort', resolve);
    let responseHandler = () => {
      tokenizedApi.activity.eventSource?.removeEventListener(
        'billing',
        responseHandler,
      );
      resolve();
    };

    tokenizedApi.activity.eventSource?.addEventListener(
      'billing',
      responseHandler,
    );
  });
}

async function subscribe(
  tokenizedApi,
  entityId,
  priceId,
  currencyCode,
  callbackUrl,
) {
  let subscriptionResult = (
    await tokenizedApi.endpoints.post(
      url`entities/${entityId}/subscribe`,
      JSON.stringify(
        jsonKeyTransform(
          { priceId, currency: currencyCode, callbackUrl },
          toSnakeCase,
        ),
      ),
    )
  ).data.data;
  if (subscriptionResult) {
    openExternalLink(subscriptionResult.url);
  }
}

async function waitForWorkspaceStatus(tokenizedApi, entityId, priceId) {
  while (
    (await getWorkspaceStatus(tokenizedApi, null, entityId))?.priceId != priceId
  ) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}

export function useSubscribe() {
  let queryClient = useQueryClient();
  let tokenizedApi = useTokenizedApi();
  let callbackUrl = makeCallbackURL();

  return async ({ entityId, priceId, currencyCode }) => {
    await Promise.all([
      Promise.race([
        waitForBillingSSE(tokenizedApi),
        waitForWorkspaceStatus(tokenizedApi, entityId, priceId),
      ]),
      subscribe(tokenizedApi, entityId, priceId, currencyCode, callbackUrl),
    ]);

    refreshQueries(queryClient);

    // Choosing a plan can change the display currency,
    // which is part of the profile data
    tokenizedApi.entities.profilesQuery.invalidate();
  };
}

export function useRefreshWorkspaceStatus(setUpdating) {
  let queryClient = useQueryClient();

  return async () => {
    try {
      setUpdating?.(true);
      await refreshQueries(queryClient);
    } finally {
      setUpdating?.(false);
    }
  };
}

async function paySubscription(tokenizedApi, profileId, subscriptionId) {
  let hostedURL = (
    await tokenizedApi.endpoints.post(
      url`profiles/${profileId}/subscriptions/${subscriptionId}/pay`,
    )
  ).data.data;
  return hostedURL;
}

export function usePaySubscription(profileId) {
  const tokenizedApi = useTokenizedApi();
  return async (subscriptionId) => {
    openExternalLink(
      await paySubscription(tokenizedApi, profileId, subscriptionId),
    );
  };
}

async function subscribeContract(
  tokenizedApi,
  profileId,
  contractId,
  discountCode,
) {
  let subscriptionResult = (
    await tokenizedApi.endpoints.post(
      url`profiles/${profileId}/contracts/${contractId}/subscribe`,
      JSON.stringify(
        jsonKeyTransform({ contractId, discountCode }, toSnakeCase),
      ),
    )
  ).data.data;
}

export function useSubscribeContract() {
  let tokenizedApi = useTokenizedApi();

  return async (profileId, contractId, discountCode) => {
    await subscribeContract(tokenizedApi, profileId, contractId, discountCode);
    tokenizedApi.contracts.contractQuery.invalidate();
  };
}

export function useSubscribedProfiles() {
  const [profiles] = useLiquidNobleActiveProfiles();
  return profiles;
}
